import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  AfterViewInit,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import { Chart, ChartEvent, ActiveElement, registerables } from 'chart.js'
import { BubbleDataPoint } from 'chart.js'
import { Datum } from 'src/app/models/common/datum'
import { FlipPopulationData } from 'src/app/models/flip/flip-population-data'
import { FlipService } from 'src/app/services/flip/flip.service'
import { SpinnerService } from 'src/app/services/spinner/spinner.service'
import { BannerService } from 'src/app/services/banner/banner.service'
import moment from 'moment'
import { CommonService } from '../../../services/common/common.service'
import { TranslateService } from '@ngx-translate/core'

// Register all Chart.js components
Chart.register(...registerables)

interface FlipPopulationClient {
  clientId?: string
  clientName: string
  age: string
  dateOfBirth: string
  caseManagerName: string
  totalScore?: number // Add this
  ageInMonths?: number // Add this
}

@Component({
  selector: 'app-flip-population-graph',
  templateUrl: './flip-population-graph.component.html',
  styleUrls: ['./flip-population-graph.component.scss'],
  standalone: false,
})
export class FlipPopulationGraphComponent implements OnInit, AfterViewInit {
  @ViewChild('populationChart') chartCanvas: ElementRef<HTMLCanvasElement>

  private chart: Chart
  dataLoaded = false
  private viewInitialized = false

  // Form controls for filters
  clientFilterControl = new FormControl<string>('all')
  caseManagerFilterControl = new FormControl<string>('all')

  // Data
  rawData: FlipPopulationData
  filteredClients: FlipPopulationClient[] = []

  // Dropdown options
  clientOptions: Datum[] = []
  caseManagerOptions: Datum[] = []

  // Table columns
  displayedColumns: string[] = [
    'clientName',
    'age',
    'dateOfBirth',
    'caseManagerName',
  ]

  // Selected data point
  selectedAgeRange: { min: number; max: number } | null = null
  // Update the selected range interface to include score and ageInMonths
  // Selected filter for chart point clicks
  selectedScoreFilter: { totalScore: number; ageInMonths: number } | null = null

  constructor(
    private spinnerService: SpinnerService,
    private bannerService: BannerService,
    private flipService: FlipService,
    private commonService: CommonService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.initialiseData()

    this.translateService.onLangChange.subscribe(() => {
      this.tryCreateChart()
    })
  }

  initialiseData(): void {
    // Load data first
    this.loadData()

    // Subscribe to filter changes
    this.clientFilterControl.valueChanges.subscribe(() => {
      this.applyFilters()
      this.updateChart()
    })

    this.caseManagerFilterControl.valueChanges.subscribe(() => {
      this.applyFilters()
      this.updateChart()
    })
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true
    // Try to create chart if data already loaded
    this.tryCreateChart()
  }

  private tryCreateChart(): void {
    if (
      this.dataLoaded &&
      this.viewInitialized &&
      this.chartCanvas?.nativeElement
    ) {
      setTimeout(() => {
        this.createChart()
      }, 0)
    }
  }

  loadData(): void {
    this.spinnerService.show()

    this.flipService.Population().subscribe({
      next: (data: FlipPopulationData) => {
        this.rawData = data
        this.dataLoaded = true
        this.setupFilterOptions()
        this.applyFilters()

        // Try to create chart now that data is loaded
        this.tryCreateChart()
      },
      error: (error) => {
        console.error('Error loading population data:', error)
        this.bannerService.showBanner(
          'Unable to retrieve population data.',
          'error',
          3000,
        )
        this.spinnerService.hide()
      },
      complete: () => {
        this.spinnerService.hide()
      },
    })
  }

  setupFilterOptions(): void {
    if (!this.rawData || !this.rawData.flipPopulationDto) {
      this.clientOptions = [
        { label: this.commonService.translateSafe('All'), value: 'all' },
      ]
      this.caseManagerOptions = [
        { label: this.commonService.translateSafe('All'), value: 'all' },
      ]
      return
    }

    // Setup client dropdown options
    this.clientOptions = [
      { label: this.commonService.translateSafe('All'), value: 'all' },
      ...Array.from(
        new Set(this.rawData.flipPopulationDto.map((c) => c.clientName)),
      )
        .sort((a, b) => a !== null && b !== null && a.localeCompare(b))
        .map((name) => ({ label: name, value: name })),
    ]

    // Setup case manager dropdown options
    this.caseManagerOptions = [
      { label: this.commonService.translateSafe('All'), value: 'all' },
      ...Array.from(
        new Set(this.rawData.flipPopulationDto.map((c) => c.caseManagerName)),
      )
        .sort((a, b) => a !== null && b !== null && a.localeCompare(b))
        .map((name) => ({ label: name, value: name })),
    ]
  }

  onChartClick(event: ChartEvent, activeElements: ActiveElement[]): void {
    if (!activeElements || activeElements.length === 0) {
      return
    }

    try {
      const element = activeElements[0]
      const dataPoint = this.chart.data.datasets[element.datasetIndex].data[
        element.index
      ] as BubbleDataPoint

      if (!dataPoint || dataPoint.x == null || dataPoint.y == null) {
        return
      }

      const ageInYears = dataPoint.x
      const totalScore = dataPoint.y

      const matchingCount = this.rawData.flipPopulationCounts.find(
        (point) =>
          Math.abs(point.ageInYears - ageInYears) < 0.01 &&
          point.totalScore === totalScore,
      )

      if (matchingCount) {
        this.selectedScoreFilter = {
          totalScore: matchingCount.totalScore,
          ageInMonths: matchingCount.ageInMonths,
        }

        this.selectedAgeRange = null
        this.highlightPoint(element.index)
        this.applyFilters()
      }
    } catch (error) {
      console.error('Error handling chart click:', error)
    }
  }

  highlightClickedPoint(index: number): void {
    if (!this.chart || !this.chart.data.datasets[0]) {
      return
    }

    try {
      const dataset = this.chart.data.datasets[0]
      const colors = (dataset.data as BubbleDataPoint[]).map((_, i) =>
        i === index ? 'rgba(36, 51, 131, 0.8)' : 'rgba(135, 177, 217, 0.6)',
      )
      dataset.backgroundColor = colors as any
      this.chart.update()
    } catch (error) {
      console.error('Error highlighting point:', error)
    }
  }

  applyFilters(): void {
    if (!this.rawData || !this.rawData.flipPopulationDto) {
      this.filteredClients = []
      return
    }

    const clientFilter = this.clientFilterControl.value
    const caseManagerFilter = this.caseManagerFilterControl.value

    const matchedClients = this.rawData.flipPopulationDto.filter((client) => {
      const matchesClient =
        clientFilter === 'all' || client.clientName === clientFilter
      const matchesCaseManager =
        caseManagerFilter === 'all' ||
        client.caseManagerName === caseManagerFilter

      const matchesScoreFilter = this.selectedScoreFilter
        ? client.totalScorePopulation === this.selectedScoreFilter.totalScore &&
          client.ageInMonths === this.selectedScoreFilter.ageInMonths
        : true

      return matchesClient && matchesCaseManager && matchesScoreFilter
    })

    const uniqueClientsMap = new Map<string, FlipPopulationClient>()

    matchedClients.forEach((client) => {
      const uniqueKey = client.clientId || client.clientName
      if (!uniqueClientsMap.has(uniqueKey)) {
        uniqueClientsMap.set(uniqueKey, client)
      }
    })

    this.filteredClients = Array.from(uniqueClientsMap.values()).sort((a, b) =>
      a.clientName.localeCompare(b.clientName),
    )
  }

  clientMatchesAgeRange(client: FlipPopulationClient): boolean {
    if (!this.selectedAgeRange) return true

    // Convert client age to years (parse "3y 1m" format)
    const ageInYears = this.parseAgeToYears(client.age)
    return (
      ageInYears >= this.selectedAgeRange.min &&
      ageInYears < this.selectedAgeRange.max
    )
  }

  parseAgeToYears(ageString: string): number {
    const match = ageString.match(/(\d+)y\s*(\d+)m/)
    if (match) {
      const years = parseInt(match[1])
      const months = parseInt(match[2])
      return years + months / 12
    }
    return 0
  }

  getFilteredChartData(): BubbleDataPoint[] {
    if (
      !this.rawData ||
      !this.rawData.flipPopulationCounts ||
      !this.rawData.flipPopulationDto
    ) {
      console.warn('Raw data not available')
      return []
    }

    const clientFilter = this.clientFilterControl.value
    const caseManagerFilter = this.caseManagerFilterControl.value

    // If no filters applied, show all data
    if (clientFilter === 'all' && caseManagerFilter === 'all') {
      return this.rawData.flipPopulationCounts
        .filter(
          (point) =>
            point.ageInYears != null &&
            point.totalScore != null &&
            point.count != null,
        )
        .map((point) => ({
          x: point.ageInYears,
          y: point.totalScore,
          r: Math.max(point.count * 5 + 5, 5),
        }))
    }

    // Get matching clients based on filters
    const matchingClients = this.rawData.flipPopulationDto.filter((client) => {
      const matchesClient =
        clientFilter === 'all' || client.clientName === clientFilter
      const matchesCaseManager =
        caseManagerFilter === 'all' ||
        client.caseManagerName === caseManagerFilter
      return matchesClient && matchesCaseManager
    })

    if (matchingClients.length === 0) {
      console.warn('No matching clients for filters')
      return []
    }

    // Collect all unique score/age combinations from matching clients
    const matchingDataPoints = new Set<string>()
    matchingClients.forEach((client) => {
      if (client.totalScorePopulation != null && client.ageInMonths != null) {
        // Create a unique key for each data point
        const key = `${client.totalScorePopulation}_${client.ageInMonths}`
        matchingDataPoints.add(key)
      }
    })

    // Filter chart counts based on the collected data points
    const filteredCounts = this.rawData.flipPopulationCounts.filter((point) => {
      if (point.totalScore == null || point.ageInMonths == null) {
        return false
      }
      const key = `${point.totalScore}_${point.ageInMonths}`
      return matchingDataPoints.has(key)
    })

    return filteredCounts.map((point) => ({
      x: point.ageInYears,
      y: point.totalScore,
      r: Math.max(point.count * 5 + 5, 5),
    }))
  }

  createChart(): void {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }

    if (!this.chartCanvas?.nativeElement) {
      console.error('Chart canvas not available')
      return
    }

    if (!this.rawData) {
      console.error('Chart data not loaded yet')
      return
    }

    const ctx = this.chartCanvas.nativeElement.getContext('2d')
    if (!ctx) {
      console.error('Could not get 2D context from canvas')
      return
    }

    const data = this.getFilteredChartData()

    if (!data || data.length === 0) {
      console.warn('No data available for chart')
      return
    }

    try {
      this.chart = new Chart(ctx, {
        type: 'bubble',
        data: {
          datasets: [
            {
              label: 'Population Data',
              data: data,
              backgroundColor: 'rgba(135, 177, 217, 0.6)',
              borderColor: 'rgba(135, 177, 217, 1)',
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          onClick: (event, activeElements) => {
            this.onChartClick(event, activeElements)
          },
          scales: {
            x: {
              type: 'linear',
              position: 'bottom',
              title: {
                display: true,
                text: this.commonService.translateSafe('Age in Years'),
              },
              min: 0,
              max: 6,
              ticks: {
                stepSize: 1,
              },
            },
            y: {
              type: 'linear',
              title: {
                display: true,
                text: this.commonService.translateSafe('Score'),
              },
              min: 0,
              max: 70,
              ticks: {
                stepSize: 10,
              },
            },
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: true,
              callbacks: {
                label: (context) => {
                  const point = context.raw as any
                  return `Age In Years : ${point.x?.toFixed(2)}, Score: ${
                    point.y
                  }, Client Count: ${Math.round((point.r - 5) / 5)}`
                },
              },
            },
          },
        },
      })
    } catch (error) {
      console.error('Error creating chart:', error)
    }
  }

  updateChart(): void {
    if (!this.chart || !this.rawData) {
      return
    }

    try {
      const data = this.getFilteredChartData()

      if (!data || data.length === 0) {
        this.chart.data.datasets[0].data = []
      } else {
        this.chart.data.datasets[0].data = data
      }

      this.chart.update()
    } catch (error) {
      console.error('Error updating chart:', error)
    }
  }

  clearSelection(): void {
    this.selectedAgeRange = null
    this.selectedScoreFilter = null
    this.applyFilters()
    this.resetHighlight()
  }

  resetHighlight(): void {
    if (!this.chart || !this.chart.data.datasets[0]) {
      return
    }

    try {
      const dataset = this.chart.data.datasets[0]
      dataset.backgroundColor = 'rgba(135, 177, 217, 0.6)'
      this.chart.update()
    } catch (error) {
      console.error('Error resetting highlight:', error)
    }
  }

  formatDateOfBirth(dateString: string): string {
    if (!dateString) return ''
    return moment(dateString).format('DD-MMM-YYYY')
  }

  highlightPoint(index: number): void {
    if (!this.chart || !this.chart.data.datasets[0]) {
      return
    }

    try {
      const dataset = this.chart.data.datasets[0]
      const colors = (dataset.data as BubbleDataPoint[]).map((_, i) =>
        i === index ? 'rgba(36, 51, 131, 0.8)' : 'rgba(135, 177, 217, 0.6)',
      )
      dataset.backgroundColor = colors as any
      this.chart.update()
    } catch (error) {
      console.error('Error highlighting point:', error)
    }
  }
}
